/**
 * WarRoomSummaryCard — sidebar card showing War Room deliberation status.
 *
 * Receives the war_room_summary slice from
 * /api/v1/dashboard/accountant-payload/{claim_id}.
 *
 * Shows:
 *   - Whether a deliberation has run at all
 *   - Deliberation status (in_progress / completed / pending)
 *   - Participating agent roles
 *   - Event count + last activity (relative time)
 *
 * Pure presentation — no self-fetch.
 */

import React from 'react';
import { Brain, Clock, Users, Loader2, AlertCircle } from 'lucide-react';

interface WarRoomSlice {
  has_deliberation: boolean;
  status: string | null;
  participants: string[];
  event_count: number;
  last_activity: string | null;
  last_activity_relative: string;
}

interface WarRoomSummaryCardProps {
  data?: WarRoomSlice | { error: string } | null;
  isLoading?: boolean;
}

const STATUS_STYLES: Record<string, { style: React.CSSProperties; label: string }> = {
  completed:   { style: { backgroundColor: 'transparent',  color: 'var(--color-verified)' },     label: 'Completed' },
  in_progress: { style: { backgroundColor: 'var(--color-needs-human-tint)',   color: 'var(--color-needs-human-text)' },     label: 'In Progress' },
  pending:     { style: { backgroundColor: 'var(--color-needs-human-tint)',   color: 'var(--color-needs-human-text)' }, label: 'Pending' },
  failed:      { style: { backgroundColor: 'var(--color-needs-human-tint)',   color: 'var(--color-needs-human-text)' },     label: 'Failed' },
};

/** Strip the `ai:` prefix for display, e.g. "ai:forensic" → "Forensic" */
function formatParticipant(role: string): string {
  const stripped = role.replace(/^(ai:|human:)/, '');
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export default function WarRoomSummaryCard({ data, isLoading }: WarRoomSummaryCardProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>War Room</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin mr-2" style={{ color: 'var(--color-text-dim)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Loading…</span>
        </div>
      </div>
    );
  }

  if (!data || 'error' in data) {
    return (
      <div className="rounded-xl border shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <Brain className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>War Room</h3>
        </div>
        <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Unavailable</span>
        </div>
      </div>
    );
  }

  const { has_deliberation, status, participants, event_count, last_activity_relative } = data;

  if (!has_deliberation) {
    return (
      <div className="rounded-xl border shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <Brain className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>War Room</h3>
        </div>
        <p className="text-xs text-center py-2" style={{ color: 'var(--color-text-dim)' }}>No deliberation run yet</p>
      </div>
    );
  }

  const statusStyle =
    (status && STATUS_STYLES[status]) ||
    { style: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }, label: status || 'Unknown' };

  return (
    <div className="rounded-xl border shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>War Room</h3>
        </div>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={statusStyle.style}>
          {statusStyle.label}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="flex items-center gap-1.5 p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <Brain className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          <div>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Events</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{event_count}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          <div>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Last activity</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{last_activity_relative}</p>
          </div>
        </div>
      </div>

      {/* Participants */}
      {participants.length > 0 && (
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          <div className="flex flex-wrap gap-1">
            {participants.map((p) => (
              <span
                key={p}
                className="text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}
              >
                {formatParticipant(p)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
